import { CategoryTag, SkillLevelTag } from '@/types';
import { API_CONFIG } from '@/config/api';

export interface CategoryApiResponse {
  id: string;
  name: string;
  color: string;
  description?: string;
  is_active: boolean;
  sort_order: number;
  created_at: string;
  updated_at: string;
}

export interface SkillLevelApiResponse {
  id: string;
  name: string;
  color: string;
  description?: string;
  sort_order: number;
  created_at: string;
  updated_at: string;
}

export class CategoryService {
  private static baseUrl = API_CONFIG.baseUrl;
  private static categoriesCache: CategoryTag[] | null = null;
  private static skillLevelsCache: SkillLevelTag[] | null = null;
  private static cacheExpiry = 5 * 60 * 1000; // 5 minutes
  private static lastCacheTime = 0;

  /**
   * Get all categories from the backend API
   */
  static async getCategories(activeOnly: boolean = true): Promise<CategoryTag[]> {
    try {
      // Check cache first
      if (this.categoriesCache && Date.now() - this.lastCacheTime < this.cacheExpiry) {
        return activeOnly 
          ? this.categoriesCache.filter(cat => true) // All categories are active in our structure
          : this.categoriesCache;
      }



      console.log('CategoryService: Fetching categories from:', `${this.baseUrl}/api/categories?active_only=${activeOnly}`);
      const response = await fetch(`${this.baseUrl}/api/categories?active_only=${activeOnly}`, {
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        console.error('CategoryService: Failed to fetch categories:', response.status, response.statusText);
        throw new Error(`Failed to fetch categories: ${response.status} ${response.statusText}`);
      }

      const categoriesData: CategoryApiResponse[] = await response.json();
      
      // Convert API response to frontend format
      const categories: CategoryTag[] = categoriesData.map(cat => ({
        id: cat.id,
        name: cat.name,
        color: cat.color,
        description: cat.description,
        is_active: cat.is_active,
        sort_order: cat.sort_order
      }));

      // Update cache
      this.categoriesCache = categories;
      this.lastCacheTime = Date.now();

      return categories;
    } catch (error) {
      console.error('Error fetching categories:', error);
      throw error;
    }
  }

  /**
   * Get all skill levels from the backend API
   */
  static async getSkillLevels(): Promise<SkillLevelTag[]> {
    try {
      // Check cache first
      if (this.skillLevelsCache && Date.now() - this.lastCacheTime < this.cacheExpiry) {
        return this.skillLevelsCache;
      }



      console.log('CategoryService: Fetching skill levels from:', `${this.baseUrl}/api/skill-levels`);
      const response = await fetch(`${this.baseUrl}/api/skill-levels`, {
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        console.error('CategoryService: Failed to fetch skill levels:', response.status, response.statusText);
        throw new Error(`Failed to fetch skill levels: ${response.status} ${response.statusText}`);
      }

      const skillLevelsData: SkillLevelApiResponse[] = await response.json();
      
      // Convert API response to frontend format
      const skillLevels: SkillLevelTag[] = skillLevelsData.map(level => ({
        id: level.id,
        name: level.name,
        color: level.color,
        description: level.description
      }));

      // Update cache
      this.skillLevelsCache = skillLevels;
      this.lastCacheTime = Date.now();

      return skillLevels;
    } catch (error) {
      console.error('Error fetching skill levels:', error);
      throw error;
    }
  }

  /**
   * Get a specific category by ID
   */
  static async getCategoryById(categoryId: string): Promise<CategoryTag | null> {
    try {
      const response = await fetch(`${this.baseUrl}/api/categories/${categoryId}`, {
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        console.error('Failed to fetch category:', response.status);
        throw new Error(`Failed to fetch category: ${response.status}`);
      }

      const categoryData: CategoryApiResponse = await response.json();
      
      return {
        id: categoryData.id,
        name: categoryData.name,
        color: categoryData.color,
        description: categoryData.description
      };
    } catch (error) {
      console.error('Error fetching category:', error);
      throw error;
    }
  }

  /**
   * Get a specific skill level by ID
   */
  static async getSkillLevelById(skillLevelId: string): Promise<SkillLevelTag | null> {
    try {
      const response = await fetch(`${this.baseUrl}/api/skill-levels/${skillLevelId}`, {
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        console.error('Failed to fetch skill level:', response.status);
        throw new Error(`Failed to fetch skill level: ${response.status}`);
      }

      const skillLevelData: SkillLevelApiResponse = await response.json();
      
      return {
        id: skillLevelData.id,
        name: skillLevelData.name,
        color: skillLevelData.color,
        description: skillLevelData.description
      };
    } catch (error) {
      console.error('Error fetching skill level:', error);
      throw error;
    }
  }

  /**
   * Clear the cache
   */
  static clearCache(): void {
    this.categoriesCache = null;
    this.skillLevelsCache = null;
    this.lastCacheTime = 0;
  }


}

// Export singleton instance
export const categoryService = CategoryService;
